import { ExtendedFile, useProjectId, useServiceName } from '@consigli/hooks';
import { DocumentPackageType, Language, PipelineName } from '@consigli/types';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { ServiceIdIndexValue } from '@/routes';

import { LuminaireUploadInfo } from './components/luminaire-upload-info';
import { WizardAnalyzeButton } from '../components/wizard-analyze-button';
import { TabType, WizardLayout } from '../components/wizard-layout';
import { useCreateInsightPackage } from '../hooks/use-create-insight-package';
import { useUploadProgress } from '../hooks/use-upload-progress';
import { LuminaireUploadForm } from './components/luminaire-upload-form';

const FORM_STATE_KEYS = {
  ITERATION_NAME: 'iterationName',
  LANGUAGE: 'language',
} as const;

export function LuminaireScheduleWizard() {
  const { t } = useTranslation();
  const [localFiles, setLocalFiles] = useState<ExtendedFile[]>([]);
  const projectId = useProjectId();
  const serviceName = useServiceName();
  const type = ServiceIdIndexValue[serviceName] as DocumentPackageType;
  const [isProcessing, setIsProcessing] = useState(false);
  const [targetCounter, setTargetCounter] = useState(0);
  const [currentCounter, setCurrentCounter] = useState(0);

  const { createDocumentPackage, uploadFiles, startAnalysis } = useCreateInsightPackage({
    setCurrentCounter,
    setTargetCounter,
  });

  const { isUploading, progress } = useUploadProgress(currentCounter, targetCounter);

  const methods = useForm({
    defaultValues: {
      iterationName: '',
      language: '',
    },
  });

  const { getValues } = methods;

  const onAnalyze = useCallback(async () => {
    if (localFiles.length === 0) {
      toast.error(t('package-creation.schedule-file-required'));
      return;
    }

    try {
      setIsProcessing(true);

      const packageId = await createDocumentPackage(
        type,
        getValues(FORM_STATE_KEYS.ITERATION_NAME),
        getValues(FORM_STATE_KEYS.LANGUAGE) as Language,
      );

      if (packageId === undefined) return;

      await uploadFiles(localFiles, packageId);

      await startAnalysis(packageId, PipelineName.LUMINAIRE_SCHEDULE);
      toast.success(t('package-creation.success-analyzed'));
    } catch (err) {
      console.error('Error starting analysis:', err);
      toast.error(t('package-creation.failed-analyzed'));
    } finally {
      setIsProcessing(false);
    }
  }, [localFiles, t, createDocumentPackage, type, getValues, uploadFiles, startAnalysis]);

  return (
    <WizardLayout
      messageHeader={<LuminaireUploadInfo />}
      progress={progress}
      isUploading={isUploading}
      isProcessing={isProcessing}
      tabContent={{
        [TabType.CREATE_PACKAGE]: (
          <FormProvider {...methods}>
            <LuminaireUploadForm
              projectId={projectId}
              type={type}
              onSubmit={onAnalyze}
              selectedFiles={localFiles}
              setSelectedFiles={setLocalFiles}
            >
              <WizardAnalyzeButton localFiles={localFiles} type="submit" />
            </LuminaireUploadForm>
          </FormProvider>
        ),
      }}
    />
  );
}
